/**
 * EventAdminsModal — who manages an event: its owner plus co-admins.
 *
 * Any admin can see the list. The owner (creator/organiser) or a Super Admin
 * adds and removes co-admins; a co-admin can step down. Co-admins manage the
 * event exactly like its owner. Backed by /admin/events/{id}/admins.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance, getApiErrorMessage, Modal } from './_shared';

function Person({ p, badge, action }) {
  return (
    <li className="flex items-center gap-3 py-2">
      {p.profile_picture_url
        ? <img src={p.profile_picture_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
        : <i className="ph-bold ph-user-circle text-3xl text-gray-300 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{p.name}</p>
        <p className="text-xs text-gray-400 truncate">{p.email}</p>
      </div>
      {badge}
      {action}
    </li>
  );
}

export default function EventAdminsModal({ event, userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const base = `/admin/events/${event.id}`;

  const load = useCallback(async () => {
    try { setData((await axiosInstance.get(`${base}/admins`)).data); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not load admins')); }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  // Search admins to add (debounced)
  useEffect(() => {
    if (!data?.can_edit || query.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(`${base}/admin-candidates`, { params: { q: query.trim() } });
        setResults(res.data);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, base, data?.can_edit]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      await load();
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Something went wrong'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (p) => {
    if (await run(() => axiosInstance.post(`${base}/admins`, { user_id: p.user_id }), `${p.name} added as a co-admin`)) {
      setQuery('');
      setResults([]);
    }
  };

  const remove = (p) => {
    const self = p.user_id === userId;
    const msg = self
      ? `Step down as an admin of ${event.title}? You'll no longer be able to manage it.`
      : `Remove ${p.name} as an admin of ${event.title}?`;
    if (!window.confirm(msg)) return;
    if (self) {
      run(() => axiosInstance.delete(`${base}/admins/${p.user_id}`), 'You stepped down').then((ok) => ok && onClose());
    } else {
      run(() => axiosInstance.delete(`${base}/admins/${p.user_id}`), `${p.name} removed`);
    }
  };

  return (
    <Modal title="Event admins" onClose={onClose}>
      <div className="space-y-4" data-testid="event-admins-modal">
        <p className="text-xs text-gray-500">
          Co-admins manage <strong>{event.title}</strong> just like its owner — registrations, teams, auction,
          fixtures, scoring and results. Only the owner or a Super Admin can change this list.
        </p>

        {!data ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9]" />
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Owner</p>
              {data.owner ? (
                <ul><Person p={data.owner}
                  badge={<span className="text-[10px] font-bold bg-[#2B3A67]/10 text-[#2B3A67] px-1.5 py-0.5 rounded">OWNER</span>} /></ul>
              ) : <p className="text-xs text-gray-400">No owner recorded — only Super Admins manage it.</p>}
            </div>

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                Co-admins <span className="text-gray-400">{data.co_admins.length}</span>
              </p>
              {data.co_admins.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No co-admins yet.</p>
              ) : (
                <ul className="divide-y divide-gray-100" data-testid="co-admin-list">
                  {data.co_admins.map((p) => (
                    <Person key={p.user_id} p={p}
                      action={(data.can_edit || p.user_id === userId) && (
                        <button onClick={() => remove(p)} disabled={busy}
                          className="px-2 py-1.5 rounded-lg border border-red-200 text-red-500 text-xs font-bold"
                          data-testid={`remove-co-admin-${p.user_id}`}>
                          {p.user_id === userId ? 'Step down' : 'Remove'}
                        </button>
                      )} />
                  ))}
                </ul>
              )}
            </div>

            {data.can_edit && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Add a co-admin</p>
                <div className="relative">
                  <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search admins by name or email"
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white"
                    data-testid="co-admin-search" />
                </div>
                {query.trim().length >= 2 && (
                  results.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      No matching admins. Only users with the Admin role can be added — a Super Admin gives that role in Users.
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100" data-testid="co-admin-results">
                      {results.map((p) => (
                        <Person key={p.user_id} p={p}
                          action={(
                            <button onClick={() => add(p)} disabled={busy}
                              className="px-3 py-1.5 rounded-lg bg-[#2B3A67] text-white text-xs font-bold disabled:opacity-50"
                              data-testid={`add-co-admin-${p.user_id}`}>Add</button>
                          )} />
                      ))}
                    </ul>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
